class VideoFeed {
  constructor() {
    // Cache DOM elements
    this.modal = document.getElementById("video-fullscreen-modal");
    this.stackContainer = this.modal.querySelector(".video-feed-stack");
    this.modalClose = this.modal.querySelector(".modal-close");
    this.videoSlides = Array.from(
      this.stackContainer.querySelectorAll(".modal-video-slide")
    );
    this.videoWrappers = Array.from(
      document.querySelectorAll(".video-wrapper")
    );

    // State variables
    this.currentIndex = 0;
    this.isSwitching = false;
    this.scrollTimeout = null;
    this.isMobile = this.detectMobileDevice();
    this.videoPool = new Map(); // Maps video URLs to their video elements
    this.activeVideos = new Map(); // Tracks which video element is currently in which container
    this.originalPositions = new Map(); // Maps video URLs to their original wrapper indices
    this.isScrollIndicatorShown = false;

    // Check if there's a product handle in the URL
    this.parseUrlForProductHandle();

    // State to remember if we were in modal view before navigating away
    this.saveModalState();
    this.checkForReturnFromProductPage();

    // Initialize components
    this.createScrollIndicator();
    this.setupEventListeners();

    // Initialize with proper loading sequence
    this.initializeVideoLoading();
  }

  // Better mobile detection that accounts for iPads and other tablets
  detectMobileDevice() {
    // Check if device is mobile based on user agent
    const isMobileUserAgent =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    // Additional check for touch capability and screen size
    const isTouchCapable =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth <= 600;

    // Consider it mobile if either:
    // 1. It has a mobile user agent and touch capability
    // 2. It has a small screen and touch capability
    return (
      (isMobileUserAgent && isTouchCapable) || (isSmallScreen && isTouchCapable)
    );
  }

  // Parse the URL for a product handle
  parseUrlForProductHandle() {
    if (typeof NDPersonalization === 'undefined') return;
    const handle = NDPersonalization.getProductHandle();
    if (handle) {
      const targetIndex = this.findVideoIndexByProductHandle(handle);
      if (targetIndex !== -1) this.reorderVideos(targetIndex);
    }
  }

  // Find the index of a video by product handle
  findVideoIndexByProductHandle(productHandle) {
    const normalized = productHandle.trim().toLowerCase();
    for (let i = 0; i < this.videoWrappers.length; i++) {
      const wrapperHandle = (this.videoWrappers[i].dataset.productHandle || '').trim().toLowerCase();
      if (wrapperHandle === normalized) return i;
    }
    return -1;
  }

  // Reorder videos to move the target video to the top
  reorderVideos(targetIndex) {
    if (targetIndex <= 0 || targetIndex >= this.videoWrappers.length) return;

    // Get the target wrapper
    const targetWrapper = this.videoWrappers[targetIndex];

    // Remove from current position
    this.videoWrappers.splice(targetIndex, 1);

    // Insert at the beginning
    this.videoWrappers.unshift(targetWrapper);

    // Do the same for modal slides to keep them in sync
    const targetSlide = this.videoSlides[targetIndex];
    if (targetSlide) {
      this.videoSlides.splice(targetIndex, 1);
      this.videoSlides.unshift(targetSlide);
    }

    // Update the DOM order in grid
    const videoGrid = targetWrapper.parentElement;
    if (videoGrid && videoGrid.classList.contains("video-grid")) {
      videoGrid.insertBefore(targetWrapper, videoGrid.firstChild);
    }

    // Update modal slides order
    if (targetSlide && this.stackContainer) {
      this.stackContainer.insertBefore(
        targetSlide,
        this.stackContainer.firstChild
      );
    }

    // Automatically open the modal if specified in URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("open") === "true") {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        this.openModal(0);
      }, 500);
    }
  }

  // Save modal state before navigating away
  saveModalState() {
    // Add click listeners to all product links inside the modal
    const productLinks = this.modal.querySelectorAll(
      ".modal-product-image-link, .modal-product-details"
    );
    productLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        // Only intercept if modal is currently visible
        if (this.modal.style.display === "flex") {
          // Save state before navigating
          localStorage.setItem("videoFeedModalOpen", "true");
          localStorage.setItem(
            "videoFeedModalIndex",
            this.currentIndex.toString()
          );

          // Don't prevent default navigation
          // The browser will follow the link after we save the state
        }
      });
    });

    // Listen for beforeunload event as a backup (for add to cart button clicks)
    window.addEventListener("beforeunload", () => {
      if (this.modal.style.display === "flex") {
        localStorage.setItem("videoFeedModalOpen", "true");
        localStorage.setItem(
          "videoFeedModalIndex",
          this.currentIndex.toString()
        );
      }
    });
  }

  // Check if we're returning from a product page
  checkForReturnFromProductPage() {
    if (localStorage.getItem("videoFeedModalOpen") === "true") {
      // Get the saved index
      const savedIndex = parseInt(
        localStorage.getItem("videoFeedModalIndex") || "0",
        10
      );

      // Clear storage immediately to prevent reopening on refresh
      localStorage.removeItem("videoFeedModalOpen");
      localStorage.removeItem("videoFeedModalIndex");

      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        setTimeout(() => {
          // Make sure page is fully loaded before reopening modal
          this.openModal(savedIndex);

          // Ensure video plays
          const currentSlide = this.videoSlides[savedIndex];
          if (currentSlide) {
            const videoUrl = currentSlide.dataset.videoUrl;
            if (videoUrl) {
              const normalizedUrl = this.normalizeVideoUrl(videoUrl);
              const video = this.videoPool.get(normalizedUrl);
              if (video) {
                this.playVideoSafely(video);
              }
            }
          }
        }, 300); // Slightly longer timeout to ensure page is ready
      });
    }
  }

  createScrollIndicator() {
    const template = document.getElementById("scroll-indicator-template");
    if (!template) return;

    const indicator = template.content.cloneNode(true);
    this.modal.appendChild(indicator);
    this.scrollIndicator = this.modal.querySelector(".scroll-indicator");

    // Initialize with hidden state
    if (this.scrollIndicator) {
      this.scrollIndicator.style.display = "none";
      this.scrollIndicator.style.opacity = "0";
      this.scrollIndicator.style.pointerEvents = "none";
    }
  }

  setupEventListeners() {
    // Lazy loading for videos
    this.setupLazyLoading();

    // Set up intersection observer for playing videos as they scroll into view
    this.setupScrollPlayObserver();

    // Add event listeners to quick-add buttons
    this.setupQuickAddButtons();

    // Video wrapper clicks
    this.videoWrappers.forEach((wrapper, idx) => {
      wrapper.addEventListener("click", (event) => {
        if (!event.target.closest(".fullscreen-icon")) {
          this.openModal(idx);
        }
      });
    });

    // Fullscreen icon delegation
    document.body.addEventListener("click", (e) => {
      const icon = e.target.closest(".fullscreen-icon");
      if (icon) {
        e.preventDefault();
        const wrapper = icon.closest(".video-wrapper");
        if (wrapper) {
          const idx = this.videoWrappers.indexOf(wrapper);
          if (idx !== -1) this.openModal(idx);
        }
      }
    });

    // Modal events
    this.modalClose.addEventListener("click", () => this.closeModal());
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) this.closeModal();
    });

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (this.modal.style.display === "flex") {
        if (e.key === "Escape") this.closeModal();
        if (e.key === "ArrowUp") {
          e.preventDefault();
          this.scrollToSlide(this.currentIndex - 1);
        }
        if (e.key === "ArrowDown") {
          e.preventDefault();
          this.scrollToSlide(this.currentIndex + 1);
        }
      }
    });

    // Arrow navigation
    const arrowUp = this.modal.querySelector(".modal-arrow-up");
    const arrowDown = this.modal.querySelector(".modal-arrow-down");
    if (arrowUp) {
      arrowUp.addEventListener("click", (e) => {
        e.stopPropagation();
        this.scrollToSlide(this.currentIndex - 1);
      });
    }
    if (arrowDown) {
      arrowDown.addEventListener("click", (e) => {
        e.stopPropagation();
        this.scrollToSlide(this.currentIndex + 1);
      });
    }

    // Scroll events
    this.setupScrollEvents();

    // Handle window resize
    this.setupResizeHandler();
  }

  setupScrollEvents() {
    // Debounce scroll events
    this.stackContainer.addEventListener("scroll", () => {
      if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.updateActiveSlide();
        this.removeScrollIndicator();
      }, 80);
    });

    // Handle events that should dismiss the indicator
    ["touchstart", "mousedown", "wheel", "scroll", "click"].forEach(
      (eventType) => {
        [this.stackContainer, this.modal].forEach((element) => {
          element.addEventListener(
            eventType,
            this.handleDismissEvent.bind(this),
            { passive: true }
          );
        });
      }
    );

    // Add mouse wheel support for desktop
    this.modal.addEventListener(
      "wheel",
      (e) => {
        if (this.modal.style.display === "flex") {
          // Prevent default scroll behavior
          e.preventDefault();

          // Determine scroll direction
          const direction = e.deltaY > 0 ? 1 : -1;

          // Navigate based on scroll direction
          this.scrollToSlide(this.currentIndex + direction);
        }
      },
      { passive: false }
    );

    // Touch handling for swipe navigation
    this.setupTouchNavigation();
  }

  handleDismissEvent(e) {
    if (this.isScrollIndicatorShown) {
      e.stopPropagation();
      this.removeScrollIndicator();
    }
  }

  setupTouchNavigation() {
    let startY = null;
    let startTime = null;

    this.stackContainer.addEventListener(
      "touchstart",
      (e) => {
        if (this.isScrollIndicatorShown) {
          e.stopPropagation();
          this.removeScrollIndicator();
        }
        startY = e.touches[0].clientY;
        startTime = Date.now();
      },
      { passive: true }
    );

    this.stackContainer.addEventListener(
      "touchmove",
      (e) => {
        if (startY === null) return;

        const currentY = e.touches[0].clientY;
        const diff = startY - currentY;

        if (Math.abs(diff) > 10 && this.isScrollIndicatorShown) {
          e.stopPropagation();
          this.removeScrollIndicator();
        }
      },
      { passive: true }
    );

    this.stackContainer.addEventListener("touchend", (e) => {
      if (startY === null) return;

      const endY = e.changedTouches[0].clientY;
      const diff = startY - endY;
      const timeDiff = Date.now() - startTime;

      // Only trigger for quick swipes (less than 300ms)
      if (timeDiff < 300) {
        if (diff > 50) this.scrollToSlide(this.currentIndex + 1); // Swipe up
        if (diff < -50) this.scrollToSlide(this.currentIndex - 1); // Swipe down
      }

      startY = null;
      startTime = null;
    });
  }

  setupLazyLoading() {
    if (!("IntersectionObserver" in window)) return;

    const options = {
      root: null,
      rootMargin: "50px 0px",
      threshold: 0.1,
    };

    const lazyVideoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const wrapper = entry.target;
          const videoUrl = wrapper.dataset.videoUrl;
          if (!videoUrl) return;

          // Store original position with normalized URL as key
          const wrapperIndex = this.videoWrappers.indexOf(wrapper);
          if (wrapperIndex !== -1) {
            const normalizedUrl = this.normalizeVideoUrl(videoUrl);
            this.originalPositions.set(normalizedUrl, wrapperIndex);
          }

          // Get or create video element and move it to the wrapper
          const videoContainer =
            wrapper.querySelector(".video-container") || wrapper;
          this.moveVideoToContainer(videoUrl, videoContainer);

          lazyVideoObserver.unobserve(wrapper);
        }
      });
    }, options);

    this.videoWrappers.forEach((wrapper) => {
      lazyVideoObserver.observe(wrapper);
    });
  }

  // Get or create a video element with optimized settings
  getVideoElement(videoUrl) {
    // Normalize the URL to handle Shopify's different sized versions
    // Extract the base URL path without size parameters
    const normalizedUrl = this.normalizeVideoUrl(videoUrl);

    if (!this.videoPool.has(normalizedUrl)) {
      console.log("Creating new video for:", normalizedUrl);

      const video = document.createElement("video");
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = "none"; // Changed from auto to none - load only when needed
      video.setAttribute("playsinline", ""); // Ensure iOS playback
      video.setAttribute("webkit-playsinline", ""); // Legacy iOS support

      // Create source element
      const source = document.createElement("source");
      source.src = videoUrl;
      source.type = "video/mp4"; // Explicitly set type
      video.appendChild(source);

      // Set poster if available
      const wrapper = this.videoWrappers.find(
        (w) => w.dataset.videoUrl === videoUrl
      );
      if (wrapper) {
        const existingVideo = wrapper.querySelector("video");
        if (existingVideo && existingVideo.poster) {
          video.poster = existingVideo.poster;
        }
      }

      // Add error handling
      video.addEventListener("error", (e) => {
        console.error("Video error:", videoUrl, e);
      });

      // Add loadeddata event to confirm successful loading
      video.addEventListener("loadeddata", () => {
        console.log("Video loaded:", videoUrl);
        // Make sure we play once loaded
        this.playVideoSafely(video);
      });

      // Default to opacity 0 until loaded
      video.style.opacity = "0";
      video.style.transition = "opacity 0.3s ease";

      // Add to pool with the normalized URL
      this.videoPool.set(normalizedUrl, video);
    }

    return this.videoPool.get(normalizedUrl);
  }

  // Helper to normalize video URLs by removing Shopify size parameters
  normalizeVideoUrl(url) {
    if (!url) return "";

    // Most Shopify video URLs follow a pattern like:
    // https://cdn.shopify.com/videos/c/vp/a1b2c3d4e5f6/a1b2c3d4e5f6.HD-1080p-7.2Mbps.mp4
    // We want to extract just the video ID or the main part

    try {
      // For URLs with definite Shopify format
      if (url.includes("cdn.shopify.com/videos")) {
        // Extract just the video ID portion
        const matches = url.match(/\/([a-zA-Z0-9]+)(\.HD-[\w-]+\.mp4|\.mp4)/);
        if (matches && matches[1]) {
          return matches[1]; // Just use the video ID as a key
        }
      }

      // Remove any query parameters and size info
      const urlWithoutParams = url.split("?")[0];

      return urlWithoutParams;
    } catch (e) {
      console.error("Error normalizing URL:", e);
      return url; // Fall back to the original URL
    }
  }

  // Move a video element efficiently
  moveVideoToContainer(videoUrl, targetContainer) {
    // Get normalized URL
    const normalizedUrl = this.normalizeVideoUrl(videoUrl);

    const video = this.getVideoElement(videoUrl);
    const currentContainer = this.activeVideos.get(video);

    // Only do work if we're actually moving the video
    if (currentContainer === targetContainer) {
      // Already in the right place, nothing to do
      return video;
    }

    // Remove from current container if it exists
    if (currentContainer) {
      try {
        currentContainer.removeChild(video);
      } catch (e) {
        console.warn("Error removing video from container:", e);
      }
    }

    // Add to new container
    targetContainer.appendChild(video);
    this.activeVideos.set(video, targetContainer);

    // Make sure video is visible
    video.style.opacity = "1";

    // Play the video
    this.playVideoSafely(video);

    return video;
  }

  // Play video with proper error handling
  playVideoSafely(video) {
    if (!video) return;

    // Make sure video is set up correctly
    video.muted = true;
    video.loop = true;
    video.playsInline = true;

    // For mobile, ensure we start from beginning for better UX
    if (
      this.isMobile &&
      video.currentTime > 0 &&
      video.currentTime >= video.duration - 0.5
    ) {
      video.currentTime = 0;
    }

    // Only play if not already playing
    if (video.paused) {
      // Load the video if it hasn't been loaded already
      if (video.readyState === 0) {
        video.load();
      }

      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.warn("Play error:", error);
          // Simple retry with short delay
          setTimeout(() => {
            video.muted = true; // Ensure muted for autoplay
            video.play().catch((e) => {
              console.warn("Retry play failed:", e);
            });
          }, 100);
        });
      }
    }
  }

  showScrollIndicator() {
    if (!this.scrollIndicator) return;

    // Check if user has seen the indicator before
    const deviceId = this.getDeviceId();
    const hasSeenIndicator = localStorage.getItem(
      `hasSeenScrollIndicator_${deviceId}`
    );

    if (!hasSeenIndicator) {
      // Add desktop/mobile specific class to help with styling
      if (!this.isMobile) {
        this.scrollIndicator.classList.add("desktop-view");
      } else {
        this.scrollIndicator.classList.add("mobile-view");
      }

      this.scrollIndicator.style.display = "flex";
      this.scrollIndicator.style.opacity = "1";
      this.scrollIndicator.style.pointerEvents = "auto";
      this.isScrollIndicatorShown = true;

      // Add click listener to dismiss
      this.scrollIndicator.addEventListener("click", (e) => {
        e.stopPropagation();
        this.removeScrollIndicator();
      });
    }
  }

  removeScrollIndicator() {
    if (!this.scrollIndicator || !this.isScrollIndicatorShown) return;

    this.scrollIndicator.style.opacity = "0";
    this.scrollIndicator.style.pointerEvents = "none";

    // Use animation frame for smoother transition
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.scrollIndicator.style.display = "none";
        this.isScrollIndicatorShown = false;
      }, 300); // Match transition duration
    });

    localStorage.setItem(
      `hasSeenScrollIndicator_${this.getDeviceId()}`,
      "true"
    );
  }

  getDeviceId() {
    let deviceId = localStorage.getItem("deviceId");
    if (!deviceId) {
      deviceId = "device_" + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("deviceId", deviceId);
    }
    return deviceId;
  }

  // Check if a video exists in the pool using normalized URL
  hasVideoInPool(videoUrl) {
    if (!videoUrl) return false;
    const normalizedUrl = this.normalizeVideoUrl(videoUrl);
    return this.videoPool.has(normalizedUrl);
  }

  // Open the fullscreen modal
  openModal(idx) {
    this.currentIndex = idx;
    this.modal.style.display = "flex";
    window.scrollLock.lock();

    const currentSlide = this.videoSlides[idx];
    if (!currentSlide) return;

    const videoUrl = currentSlide.dataset.videoUrl;
    if (!videoUrl) return;

    // Preload nearby videos for smoother navigation
    this.preloadAdjacentVideos(idx);

    // Move the active video to the modal
    const videoContainer =
      currentSlide.querySelector(".video-container") || currentSlide;
    const video = this.moveVideoToContainer(videoUrl, videoContainer);

    // Reset to start and make sure it's playing
    video.currentTime = 0;
    this.playVideoSafely(video);

    // Scroll to the current slide
    this.scrollToSlide(idx, true);

    // Show scroll indicator
    this.showScrollIndicator();
  }

  preloadAdjacentVideos(idx) {
    // Limit to adjacent videos only
    const indicesToLoad = [idx];
    if (idx > 0) indicesToLoad.push(idx - 1);
    if (idx < this.videoSlides.length - 1) indicesToLoad.push(idx + 1);

    // Just create the video elements but don't start playing all of them
    indicesToLoad.forEach((i) => {
      const slide = this.videoSlides[i];
      if (!slide) return;

      const videoUrl = slide.dataset.videoUrl;
      if (!videoUrl) return;

      // Just ensure the video is in the pool
      this.getVideoElement(videoUrl);
    });
  }

  closeModal() {
    this.modal.style.display = "none";
    window.scrollLock.unlock();

    // Hide scroll indicator
    if (this.isScrollIndicatorShown) {
      this.removeScrollIndicator();
    }

    // Clear saved state from localStorage since we're intentionally closing the modal
    localStorage.removeItem("videoFeedModalOpen");
    localStorage.removeItem("videoFeedModalIndex");

    // Move videos back to their original positions efficiently
    this.returnVideosToGrid();

    // Set a small timeout to ensure videos start playing after the modal closes
    // This helps with iOS devices that might suspend video playback
    setTimeout(() => {
      this.playVisibleGridVideos();
    }, 100);
  }

  returnVideosToGrid() {
    // Process videos that have been loaded
    for (const [
      normalizedUrl,
      originalIndex,
    ] of this.originalPositions.entries()) {
      const video = this.videoPool.get(normalizedUrl);

      if (!video) continue;

      const wrapper = this.videoWrappers[originalIndex];
      if (!wrapper) continue;

      // Get the container to place the video in
      const videoContainer =
        wrapper.querySelector(".video-container") || wrapper;

      // Get original URL from wrapper for moving
      const videoUrl = wrapper.dataset.videoUrl;
      if (!videoUrl) continue;

      // Move the video back to its original position
      this.moveVideoToContainer(videoUrl, videoContainer);
    }

    // Play visible videos
    this.playVisibleGridVideos();
  }

  playVisibleGridVideos() {
    // Get wrappers that are currently visible
    const visibleWrappers = this.getVisibleWrappers();

    // Play videos in visible wrappers
    visibleWrappers.forEach((wrapper) => {
      const videoUrl = wrapper.dataset.videoUrl;
      if (!videoUrl) return;

      // Get normalized URL
      const normalizedUrl = this.normalizeVideoUrl(videoUrl);
      const video = this.videoPool.get(normalizedUrl);

      if (!video) return;

      // Play the video
      this.playVideoSafely(video);
    });

    // For mobile, pause non-visible videos
    if (this.isMobile) {
      this.pauseNonVisibleVideos(visibleWrappers);
    }
  }

  pauseBackgroundVideos() {
    // Pause all videos except the ones visible in viewport
    this.pauseNonVisibleVideos();
  }

  pauseNonVisibleVideos(visibleWrappers) {
    const visibleUrls = visibleWrappers
      .map((wrapper) => {
        const url = wrapper.dataset.videoUrl;
        return url ? this.normalizeVideoUrl(url) : null;
      })
      .filter((url) => url !== null);

    // Pause videos that aren't in visible wrappers
    this.videoPool.forEach((video, normalizedUrl) => {
      if (!visibleUrls.includes(normalizedUrl) && !video.paused) {
        video.pause();
      }
    });
  }

  getVisibleWrappers() {
    const visibleWrappers = [];

    // Use viewport intersection to determine visibility
    const viewportHeight = window.innerHeight;
    this.videoWrappers.forEach((wrapper) => {
      const rect = wrapper.getBoundingClientRect();
      // Consider an element visible if at least part of it is in the viewport
      if (rect.top < viewportHeight && rect.bottom > 0) {
        visibleWrappers.push(wrapper);
      }
    });

    // If somehow nothing is visible, assume first few are
    if (visibleWrappers.length === 0 && this.videoWrappers.length > 0) {
      const visibleCount = Math.min(3, this.videoWrappers.length);
      for (let i = 0; i < visibleCount; i++) {
        visibleWrappers.push(this.videoWrappers[i]);
      }
    }

    return visibleWrappers;
  }

  scrollToSlide(idx, instant = false) {
    if (this.isSwitching && !instant) return;
    if (idx < 0 || idx >= this.videoSlides.length) return;

    this.isSwitching = true;
    this.currentIndex = idx;

    const slide = this.videoSlides[idx];
    if (!slide) {
      this.isSwitching = false;
      return;
    }

    // Preload current video and adjacent videos
    this.loadModalSlideVideos(idx);

    // Use more efficient scroll approach
    const top = slide.offsetTop;
    this.stackContainer.scrollTo({
      top,
      behavior: instant ? "auto" : "smooth",
    });

    // Update after scroll completes
    const scrollDuration = instant ? 50 : 350;
    setTimeout(() => {
      this.updateActiveSlide();
      this.isSwitching = false;
    }, scrollDuration);
  }

  loadModalSlideVideos(idx) {
    // Determine which slides to load
    const indicesToLoad = [idx];
    if (idx > 0) indicesToLoad.push(idx - 1);
    if (idx < this.videoSlides.length - 1) indicesToLoad.push(idx + 1);

    indicesToLoad.forEach((i) => {
      const slide = this.videoSlides[i];
      if (!slide) return;

      const videoUrl = slide.dataset.videoUrl;
      if (!videoUrl) return;

      const videoContainer = slide.querySelector(".video-container") || slide;

      // Move the video to the slide
      const video = this.moveVideoToContainer(videoUrl, videoContainer);

      // Only play the active slide
      if (i === idx) {
        video.style.opacity = "1";

        // Start from beginning for clean experience
        video.currentTime = 0;

        // Play current video
        this.playVideoSafely(video);
      } else {
        // Pause non-active videos
        video.pause();
      }
    });
  }

  updateActiveSlide() {
    // Find the slide closest to the viewport top
    let activeIdx = -1;
    let minDistFromTop = Infinity;

    const stackRect = this.stackContainer.getBoundingClientRect();

    this.videoSlides.forEach((slide, idx) => {
      const slideRect = slide.getBoundingClientRect();
      const distFromTop = Math.abs(slideRect.top - stackRect.top);

      if (distFromTop < minDistFromTop) {
        minDistFromTop = distFromTop;
        activeIdx = idx;
      }
    });

    // Default to first slide if none found
    if (activeIdx === -1 && this.videoSlides.length > 0) {
      activeIdx = 0;
    }

    // Update only if the active slide changed
    if (activeIdx !== -1 && this.currentIndex !== activeIdx) {
      this.currentIndex = activeIdx;
      this.loadModalSlideVideos(activeIdx);
    }

    // Update video playback states
    this.updateVideoPlaybackStates();

    // Update navigation arrows
    this.updateArrowStates();
  }

  updateVideoPlaybackStates() {
    // Play only the active slide's video
    this.videoSlides.forEach((slide, idx) => {
      const videoUrl = slide.dataset.videoUrl;
      if (!videoUrl) return;

      // Use normalized URL
      const normalizedUrl = this.normalizeVideoUrl(videoUrl);
      const video = this.videoPool.get(normalizedUrl);

      if (!video) return;

      if (idx === this.currentIndex) {
        // Play active video if it's paused
        if (video.paused) {
          this.playVideoSafely(video);
        }
      } else {
        // Pause all other videos
        if (!video.paused) {
          video.pause();
        }
      }
    });
  }

  updateArrowStates() {
    const arrowUp = this.modal.querySelector(".modal-arrow-up");
    const arrowDown = this.modal.querySelector(".modal-arrow-down");

    if (arrowUp) {
      arrowUp.classList.toggle("disabled", this.currentIndex === 0);
    }

    if (arrowDown) {
      arrowDown.classList.toggle(
        "disabled",
        this.currentIndex === this.videoSlides.length - 1
      );
    }
  }

  setupScrollPlayObserver() {
    if (!("IntersectionObserver" in window)) return;

    // Create observer for playing/pausing as videos enter/exit viewport
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.3,
    };

    this.scrollPlayObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const wrapper = entry.target;
        const videoUrl = wrapper.dataset.videoUrl;
        if (!videoUrl) return;

        const video = this.videoPool.get(videoUrl);
        if (!video) return;

        if (entry.isIntersecting) {
          // Video wrapper is visible, play the video
          this.playVideoSafely(video);
        } else if (
          !this.modal.style.display ||
          this.modal.style.display === "none"
        ) {
          // Only pause if we're not in modal view
          video.pause();
        }
      });
    }, options);

    // Observe all video wrappers
    this.videoWrappers.forEach((wrapper) => {
      this.scrollPlayObserver.observe(wrapper);
    });
  }

  setupQuickAddButtons() {
    // Find all add-to-cart buttons in the modal
    const quickAddButtons = this.modal.querySelectorAll(".quick-add button");

    quickAddButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        // Don't do anything special, but ensure we save the state
        if (this.modal.style.display === "flex") {
          localStorage.setItem("videoFeedModalOpen", "true");
          localStorage.setItem(
            "videoFeedModalIndex",
            this.currentIndex.toString()
          );
        }
      });

      // Find the form that contains this button
      const form = button.closest("form");
      if (form) {
        form.addEventListener("submit", (e) => {
          // Save state when form is submitted (add to cart)
          if (this.modal.style.display === "flex") {
            localStorage.setItem("videoFeedModalOpen", "true");
            localStorage.setItem(
              "videoFeedModalIndex",
              this.currentIndex.toString()
            );
          }
        });
      }
    });
  }

  // Preload all grid videos when page loads
  preloadAllVideos() {
    // Preload first few videos for better initial experience
    const preloadCount = Math.min(4, this.videoWrappers.length);

    // Create a list of URLs to preload
    const videoUrls = [];

    for (let i = 0; i < preloadCount; i++) {
      const wrapper = this.videoWrappers[i];
      if (!wrapper) continue;

      const videoUrl = wrapper.dataset.videoUrl;
      if (!videoUrl) continue;

      videoUrls.push(videoUrl);
    }

    // Preload with slight delay between each to avoid network congestion
    videoUrls.forEach((videoUrl, i) => {
      setTimeout(() => {
        this.getVideoElement(videoUrl);
      }, i * 200);
    });
  }

  // Control the initialization sequence for better loading performance
  initializeVideoLoading() {
    // Set a flag to track initialization state
    this.isInitializing = true;

    // Preload all videos with a slight delay to allow page to render first
    setTimeout(() => {
      this.preloadAllVideos();

      // Start loading visible videos immediately, but wait for first preload
      setTimeout(() => {
        this.playVisibleGridVideos();
        this.isInitializing = false;

        // After a delay, ensure visible videos are still playing
        setTimeout(() => this.checkVideoPlaybackStatus(), 2000);
      }, 600); // Give time for first video to start loading
    }, 500);
  }

  // Check if videos are playing and retry if needed
  checkVideoPlaybackStatus() {
    // Skip if we're still initializing
    if (this.isInitializing) return;

    const visibleWrappers = this.getVisibleWrappers();

    visibleWrappers.forEach((wrapper) => {
      const videoUrl = wrapper.dataset.videoUrl;
      if (!videoUrl) return;

      const video = this.videoPool.get(videoUrl);
      if (!video) return;

      // If video should be playing but isn't, retry
      if (video.paused && video.style.opacity === "1" && !video.isLoading) {
        console.log("Found paused video that should be playing, retrying");
        this.playVideoSafely(video);
      }
    });
  }

  setupResizeHandler() {
    let resizeTimer;
    window.addEventListener("resize", () => {
      // Debounce the resize event
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // Update mobile detection
        this.isMobile = this.detectMobileDevice();

        // If the scroll indicator is showing, update its classes
        if (this.isScrollIndicatorShown && this.scrollIndicator) {
          this.scrollIndicator.classList.toggle("desktop-view", !this.isMobile);
          this.scrollIndicator.classList.toggle("mobile-view", this.isMobile);
        }
      }, 250);
    });
  }
}

// Initialize when DOM is loaded
window.addEventListener("DOMContentLoaded", () => {
  new VideoFeed();
});
